import {
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildPanelShape } from '@toolproof-core/visualization/_pure/shapes';
import type {
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toNaturalDigitAngle, toPrimitiveBandRotation } from '@/adapters/primitive/_pure/derivation/constellations';
import {
    toNaturalCanonicalZeroCollectionKey,
    toNaturalCanonicalZeroConstellationKey,
    toNaturalCanonicalZeroItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { toPrimitiveBandOpacity } from '@/adapters/primitive/_pure/derivation/wheels';
import type { Config, PrimitiveWheelRole, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

const RESULT_ROLES = new Set<PrimitiveWheelRole>([
    'sum',
    'difference',
    'product',
    'quotient',
    'remainder',
]);

type CanonicalZeroMarkerDerivations = {
    entityItemCollectionDerivations: EntityItemCollectionDerivation[];
    entityConstellationDerivations: EntityConstellationDerivation[];
};

type CanonicalZeroMarkerPlane = {
    xAxis: readonly [number, number, number];
    yAxis: readonly [number, number, number];
};

function toRadialPosition(args: {
    wheel: PrimitiveWheelState;
    radius: number;
    angle: number;
    plane?: CanonicalZeroMarkerPlane;
}): [number, number, number] {
    const xOffset = args.radius * Math.sin(args.angle);
    const yOffset = -args.radius * Math.cos(args.angle);
    const xAxis = args.plane?.xAxis ?? [1, 0, 0];
    const yAxis = args.plane?.yAxis ?? [0, 1, 0];
    return [
        args.wheel.position[0] + xAxis[0] * xOffset + yAxis[0] * yOffset,
        args.wheel.position[1] + xAxis[1] * xOffset + yAxis[1] * yOffset,
        args.wheel.position[2] + xAxis[2] * xOffset + yAxis[2] * yOffset,
    ];
}

export function buildNaturalCanonicalZeroMarkerDerivations(
    config: Config,
    wheels: readonly PrimitiveWheelState[],
    plane?: CanonicalZeroMarkerPlane,
): CanonicalZeroMarkerDerivations | null {
    const marker = config.natural.display.marker.canonicalZero;
    if (!marker.visible) return null;

    const entityItemCollectionDerivations: EntityItemCollectionDerivation[] = [];
    const entityConstellationDerivations: EntityConstellationDerivation[] = [];
    const angle = toNaturalDigitAngle(config, 0);

    for (const wheel of wheels) {
        if (wheel.variant !== undefined
            || wheel.valueKind === 'placeholder'
            || !RESULT_ROLES.has(wheel.role)) continue;

        for (const power of wheel.powers) {
            const ringInnerRadius = config.base.display.wheel.innerRadius
                + power * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap);
            const radius = ringInnerRadius + config.base.display.wheel.ringWidth / 2;
            const ref = { wheelRole: wheel.role, power };
            const itemKey = toNaturalCanonicalZeroItemKey(ref);
            entityItemCollectionDerivations.push(toEntityItemCollectionDerivation({
                itemCollectionKey: toNaturalCanonicalZeroCollectionKey(ref),
                itemDerivations: [defineEntityItemDerivation({
                    item: {
                        itemKind: 'entity',
                        itemKey,
                        name: `${wheel.role} 10^${power} canonical zero`,
                        description: 'Fixed dot marking the receiver band\'s canonical zero slot.',
                    },
                    parametricState: {
                        shape: buildPanelShape({ width: 1, height: 1 }),
                        visualStyle: {
                            color: marker.color,
                            opacity: marker.opacity * toPrimitiveBandOpacity(wheel, power),
                        },
                    },
                })],
            }));
            entityConstellationDerivations.push(defineEntityConstellationDerivation({
                constellation: {
                    constellationKind: 'entity',
                    constellationKey: toNaturalCanonicalZeroConstellationKey(ref),
                    itemCollectionKey: toNaturalCanonicalZeroCollectionKey(ref),
                    anchor: { itemKey, anchorKey: 'center' },
                    meta: { wheelRole: wheel.role, power },
                },
                // A lone item in a sector ring with no inner radius renders as a flat disc in the band plane.
                parametricState: {
                    layout: {
                        layoutKind: 'sector-ring',
                        anchor: {
                            anchorKind: 'position',
                            position: toRadialPosition({ wheel, radius, angle, plane }),
                        },
                        innerRadius: 0,
                        outerRadius: marker.radius,
                        angularGapFraction: 0,
                        arcSegments: config.base.display.wheel.arcSegments,
                        rotation: toPrimitiveBandRotation(0),
                    },
                },
            }));
        }
    }

    if (entityItemCollectionDerivations.length === 0) return null;

    return { entityItemCollectionDerivations, entityConstellationDerivations };
}